import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import { AllUserPlans } from '../Customers';
import { UserModel } from '../../../../interfaces/user.interface';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';

export interface ImpersonationTokenResponse {
  accessToken: string;
  redirectPath?: string;
}

export const getAllUsers = async (
  currentPage: number,
  sortBy: string,
  sortOrder: string,
  role?: string,
  search?: string,
  status?: string
) => {
  try {
    let url = `admin/user/customers?pageNumber=${currentPage}&sortBy=${sortBy}&sortByValue=${sortOrder}`;
    if (role) url += `&role=${role}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    const data = await callAPI(url);
    return {
      users: data?.users || [],
      totalPages: data?.totalPages || 0,
      totalRecords: data?.totalRecords || 0,
    };
  } catch (error) {
    Toast('Error fetching users', true);
    throw error;
  }
};

export const searchUsers = async (searchText: string) => {
  try {
    return await callAPI(`admin/user/customers/search?search=${searchText}`);
  } catch (error) {
    Toast('Error searching users', true);
    throw error;
  }
};

export const getTotalUsers = async (): Promise<AllUserPlans | undefined> => {
  try {
    return await callAPI(`admin/user/admin-user-plans`);
  } catch (error) {
    Toast('Error fetching user plans', true);
    throw error;
  }
};

export const getActiveUsers = async (selectedPlanId: string) => {
  try {
    return await callAPI(`admin/user/admin-user-plans?id=${selectedPlanId}`);
  } catch (error) {
    Toast('Error fetching active users', true);
    throw error;
  }
};

export const deleteUser = async (email: string) => {
  try {
    const response = await callAPI(`admin/user/delete`, { email });
    if (response) {
      Toast('User deleted successfully');
      return true;
    }
  } catch (error) {
    Toast('User deletion failed', true);
    throw error;
  }
};

export const unsubscribeUserPlan = async (email: string) => {
  try {
    const response = await callAPI(`admin/user/plan/unsubscribe`, { email });
    if (response) {
      Toast('User unsubscribed successfully');
      return true;
    }
  } catch (error) {
    Toast('User unsubscribe failed', true);
    throw error;
  }
};

export const updateUserData = async (email: string, data: Partial<UserModel>) => {
  try {
    const response = await callAPI(`admin/user`, { ...data, email }, 'PATCH');
    if (response) {
      Toast('User data updated successfully');
      return true;
    }
  } catch (error) {
    Toast('User update data failed', true);
    throw error;
  }
};

export const updateUserStatus = async (email: string, newStatus: string): Promise<boolean> => {
  try {
    const response = await callAPI(`admin/user/update-status`, {
      email: email,
      status: newStatus
    });
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('Status Updated Successfully');
      return true;
    } else {
      Toast(response?.message || 'Failed To Update Status', true);
      return false;
    }
  } catch (error) {
    Toast('Failed To Update Status', true);
    throw error;
  }
};

export const generateImpersonationToken = async (userId: string): Promise<ImpersonationTokenResponse> => {
  try {
    const response = await callAPI(`admin/user/impersonate`, {
      userId: userId
    });
    if (response?.status === HTTPStatusEnum.OK) {
      return {
        accessToken: response.accessToken,
        redirectPath: response.redirectPath || '/',
      };
    } else {
      Toast(response?.message || 'Failed to generate impersonation token', true);
      throw new Error(response?.message || 'Failed to generate impersonation token');
    }
  } catch (error) {
    Toast('Failed to generate impersonation token', true);
    throw error;
  }
};